import type { ClinicClock } from "@/lib/clinic-clock";

import type { ChargeItem, ChargeTableItem } from "@/types/charge";
import { ChargeStatus } from "@/types/enums";
import { ItemType } from "@/types/enums/item-type";
import { PaymentStatus } from "@/types/enums/payment";

export interface PurchaseRow {
  key: string;
  item: ChargeItem;
  date?: string;
  locationName?: string;
  isReversed: boolean;
  /** What a reversed row says: a void never took the patient's money, a refund gave it back. */
  reversedLabel?: "Voided" | "Refunded";
  quantity: number;
  unitPrice: number;
  originalCents: number;
  discountCents: number;
  taxCents: number;
  /** What the patient paid for the line: after discounts, tax included. */
  finalCents: number;
}

/** Attempts where no money ever moved, so they don't date a purchase. */
const ABANDONED_PAYMENT_STATUSES: PaymentStatus[] = [
  PaymentStatus.FAILED,
  PaymentStatus.CANCELED,
];

/**
 * When the patient paid a charge: its earliest payment still in flight or
 * settled. Earliest rather than latest so a payment-plan purchase is dated the
 * day it was bought, not the day the final installment cleared. Undefined when
 * the charge never took a payment (voids, write-offs).
 */
function chargePaymentDate(charge: ChargeTableItem): string | undefined {
  let earliest: number | undefined;
  for (const payment of charge.payments ?? []) {
    if (ABANDONED_PAYMENT_STATUSES.includes(payment.status)) continue;
    const time = new Date(payment.createdDate).getTime();
    if (Number.isNaN(time)) continue;
    if (earliest === undefined || time < earliest) earliest = time;
  }
  return earliest === undefined ? undefined : new Date(earliest).toISOString();
}

export function flattenPurchases(charges: ChargeTableItem[]): PurchaseRow[] {
  const rows: PurchaseRow[] = [];

  for (const charge of charges) {
    if (charge.status === ChargeStatus.OUTSTANDING) continue;
    const paymentDate = chargePaymentDate(charge);
    const isReversed =
      charge.status === ChargeStatus.VOID ||
      charge.status === ChargeStatus.REFUNDED;
    for (const item of charge.items ?? []) {
      const quantity = item.quantity ?? 1;
      const unitPrice = item.price ?? 0;
      const originalCents = unitPrice * quantity;
      const discountCents =
        item.discounts?.reduce((sum, d) => sum + (d.amount ?? 0), 0) ?? 0;
      const taxCents = item.taxAmount ?? 0;
      const finalCents = Math.max(0, originalCents - discountCents) + taxCents;

      // A purchase is dated by when the patient paid. The item's own sale
      // stamp and the charge's creation date only stand in for charges with no
      // payment at all.
      const date = paymentDate ?? (item.saleDate || charge.createdDate);

      rows.push({
        key:
          item.id ?? `${charge.id}:${item.itemId ?? item.name ?? rows.length}`,
        item,
        date,
        locationName: item.locationName ?? charge.locationName,
        isReversed,
        reversedLabel: isReversed
          ? charge.status === ChargeStatus.VOID
            ? "Voided"
            : "Refunded"
          : undefined,
        quantity,
        unitPrice,
        originalCents,
        discountCents,
        taxCents,
        finalCents,
      });
    }
  }

  rows.sort((a, b) => {
    const aTime = a.date ? new Date(a.date).getTime() : 0;
    const bTime = b.date ? new Date(b.date).getTime() : 0;
    return bTime - aTime;
  });

  return rows;
}

export function purchaseItemName(item: ChargeItem): string {
  return item.name || item.item?.name || "Untitled item";
}

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  [ItemType.ITEM]: "Item",
  [ItemType.SERVICE]: "Service",
  [ItemType.PRODUCT]: "Product",
  [ItemType.PACKAGE]: "Package",
  [ItemType.MEMBERSHIP]: "Membership",
  [ItemType.MEDICATION]: "Medication",
  [ItemType.SERVICE_FEE]: "Service fee",
  [ItemType.GIFT_CARD]: "Gift card",
};

/** Plural forms, for the one-click type chips above the table. */
export const ITEM_TYPE_PLURAL_LABELS: Record<ItemType, string> = {
  [ItemType.ITEM]: "Items",
  [ItemType.SERVICE]: "Services",
  [ItemType.PRODUCT]: "Products",
  [ItemType.PACKAGE]: "Packages",
  [ItemType.MEMBERSHIP]: "Memberships",
  [ItemType.MEDICATION]: "Medications",
  [ItemType.SERVICE_FEE]: "Service fees",
  [ItemType.GIFT_CARD]: "Gift cards",
};

/** Chip order: what a clinic buys most often reads first. */
const ITEM_TYPE_ORDER: ItemType[] = [
  ItemType.PRODUCT,
  ItemType.SERVICE,
  ItemType.PACKAGE,
  ItemType.MEMBERSHIP,
  ItemType.MEDICATION,
  ItemType.GIFT_CARD,
  ItemType.SERVICE_FEE,
  ItemType.ITEM,
];

export interface PurchaseTypeFacet {
  type: ItemType;
  label: string;
  count: number;
}

/**
 * The item types present in `rows`, with how many rows each holds. Feeds the
 * quick-filter chips, so pass rows filtered by everything *except* item type:
 * the chips then stay on screen while their counts follow the other filters.
 */
export function purchaseTypeFacets(rows: PurchaseRow[]): PurchaseTypeFacet[] {
  const counts = new Map<ItemType, number>();
  for (const row of rows) {
    const type = row.item.item?.type;
    if (!type) continue;
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  return ITEM_TYPE_ORDER.filter((type) => counts.has(type)).map((type) => ({
    type,
    label: ITEM_TYPE_PLURAL_LABELS[type],
    count: counts.get(type) ?? 0,
  }));
}

/** Add or remove one type from a multiselect item-type value. */
export function toggleItemType(
  selected: ItemType[] | undefined,
  type: ItemType,
): ItemType[] | undefined {
  const current = selected ?? [];
  const next = current.includes(type)
    ? current.filter((value) => value !== type)
    : [...current, type];
  return next.length ? next : undefined;
}

/** Sentinel category value for rows whose item has no category. */
export const UNCATEGORIZED = "__uncategorized__";

export function purchaseCategory(row: PurchaseRow): string {
  const category = row.item.item?.category;
  return category && category.trim() ? category : UNCATEGORIZED;
}

export interface PurchaseFilters {
  /** Inclusive date range as local calendar day keys (YYYY-MM-DD). */
  fromDay?: string;
  toDay?: string;
  /** Category values to keep; UNCATEGORIZED matches rows with no category. Empty = all. */
  categories?: string[];
  /** Item types to keep. Empty = all. */
  itemTypes?: ItemType[];
  /** Inclusive final-price bounds, in cents. */
  minCents?: number;
  maxCents?: number;
}

/**
 * The day key (YYYY-MM-DD) for an ISO timestamp on `clock`, matching how the
 * table labels rows. Components pass `useClinicClock()`, so rows re-bucket
 * when the zone settles or changes.
 */
export function purchaseDayKey(iso: string, clock: ClinicClock): string {
  return clock.dayOf(iso);
}

export function filterPurchases(
  rows: PurchaseRow[],
  filters: PurchaseFilters,
  clock: ClinicClock,
): PurchaseRow[] {
  const { fromDay, toDay, categories, itemTypes, minCents, maxCents } = filters;

  return rows.filter((row) => {
    if (fromDay || toDay) {
      if (!row.date) return false;
      const day = purchaseDayKey(row.date, clock);
      if (fromDay && day < fromDay) return false;
      if (toDay && day > toDay) return false;
    }

    if (categories?.length && !categories.includes(purchaseCategory(row))) {
      return false;
    }

    if (itemTypes?.length) {
      const type = row.item.item?.type;
      if (!type || !itemTypes.includes(type)) return false;
    }

    if (minCents != null && row.finalCents < minCents) return false;
    if (maxCents != null && row.finalCents > maxCents) return false;

    return true;
  });
}

export interface PurchaseTotals {
  /** Rows counted, reversed ones included — it labels what is on screen. */
  itemCount: number;
  quantity: number;
  originalCents: number;
  discountCents: number;
  taxCents: number;
  finalCents: number;
}

/**
 * Money totals for a set of rows. Rows from a voided or refunded charge still
 * show in the table but contribute nothing, so a reversal can't inflate what
 * the patient actually spent.
 */
export function sumPurchases(rows: PurchaseRow[]): PurchaseTotals {
  const totals: PurchaseTotals = {
    itemCount: rows.length,
    quantity: 0,
    originalCents: 0,
    discountCents: 0,
    taxCents: 0,
    finalCents: 0,
  };

  for (const row of rows) {
    if (row.isReversed) continue;
    totals.quantity += row.quantity;
    totals.originalCents += row.originalCents;
    totals.discountCents += row.discountCents;
    totals.taxCents += row.taxCents;
    totals.finalCents += row.finalCents;
  }

  return totals;
}

export interface PurchaseDateGroup {
  /** ISO date string used for sorting; rows with no date land in the "Undated" bucket. */
  dateKey: string;
  /** Human-readable label, or "Undated" when no date is present. */
  label: string;
  rows: PurchaseRow[];
  /** Totals for this day, shown on the collapsed group row. */
  totals: PurchaseTotals;
}

/**
 * Group rows by local calendar day (YYYY-MM-DD), preserving newest-first order.
 * Rows without a date are bundled into a trailing "Undated" group.
 *
 * Keys off the same local day the rows are labeled and filtered by. Slicing the
 * UTC ISO string instead would bucket an evening purchase under the following
 * day while still labeling it with the current one.
 */
export function groupPurchasesByDate(
  rows: PurchaseRow[],
  formatLabel: (iso: string) => string,
  clock: ClinicClock,
): PurchaseDateGroup[] {
  const groups = new Map<string, Omit<PurchaseDateGroup, "totals">>();

  for (const row of rows) {
    const dateKey = row.date ? purchaseDayKey(row.date, clock) : "";
    const existing = groups.get(dateKey);
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.set(dateKey, {
        dateKey,
        label: dateKey ? formatLabel(row.date!) : "Undated",
        rows: [row],
      });
    }
  }

  return Array.from(groups.values())
    .sort((a, b) => {
      if (!a.dateKey) return 1;
      if (!b.dateKey) return -1;
      return b.dateKey.localeCompare(a.dateKey);
    })
    .map((group) => ({ ...group, totals: sumPurchases(group.rows) }));
}
